import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { usePageMeta } from '@/hooks/useSEO';
import ConsultationForm from '@/components/common/ConsultationForm';
import {
  ArrowRight, ArrowUpRight, Sparkles, Shield, X, CheckCircle2, Brain,
  Compass, GraduationCap, FileText, Plane, Briefcase, TrendingUp, Quote,
  Award, Users, Calendar, LineChart, BadgeCheck, AlertTriangle, Phone,
} from 'lucide-react';

/**
 * WhyChooseZafcoStudyAbroadPage — `/study-abroad/why-choose-zafco/`
 * ───────────────────────────────────────────────────────────────────────
 * Companion to AboutZafcoStudyAbroadPage. Establishes Zafco as a
 * "Category of One" (Migration Intelligence vs simple admissions).
 *
 * Structure (per user spec):
 *   1. Hero — H1 + sub-headline "Built on Global Mobility, Not Just Admissions"
 *   2. Side-by-Side Comparison — Admissions Agency vs Global Mobility Partner
 *   3. Trust & Compliance — MEA approval box
 *   4. Migration Intelligence — 4 question cards + mid-page CTA
 *   5. Career-First, Country-Neutral guidance
 *   6. End-to-End Support — 6-step horizontal process flow
 *   7. Ethical Consulting — high-contrast callout
 *   8. Data-Backed Success
 *   9. Community / Events
 *  10. Final CTA
 */

const COUNTRIES = [
  { flag: '🇬🇧', name: 'UK',         href: '/study-abroad/uk/' },
  { flag: '🇨🇦', name: 'Canada',     href: '/study-abroad/canada/' },
  { flag: '🇦🇺', name: 'Australia',  href: '/study-abroad/australia/' },
  { flag: '🇮🇪', name: 'Ireland',    href: '/study-abroad/ireland/' },
  { flag: '🇦🇪', name: 'Dubai',      href: '/study-abroad/dubai/' },
];

const COMPARISON = [
  { dim: 'Mission',         a: 'Get an offer letter',                              z: 'Engineer a career migration plan' },
  { dim: 'Country choice',  a: 'Whoever pays highest commission',                  z: 'Filtered on work rights, PR, and labour-market data' },
  { dim: 'After visa',      a: 'Disappears post-grant',                            z: 'Continues across post-landing + early career' },
  { dim: 'Course selection',a: 'From a fixed catalog',                             z: 'Mapped to shortage lists + sector demand' },
  { dim: 'Documents',       a: 'Will accept manipulated finances',                 z: 'Genuine only — refuses fake bank statements / experience' },
  { dim: 'Pricing',         a: 'Hidden "processing" fees surface later',           z: 'All fees disclosed before signing — no surprises' },
];

const MIGRATION_QUESTIONS = [
  { Icon: TrendingUp, q: 'Which sectors are hiring in 2026?', a: 'We benchmark against AI, healthcare, engineering, finance, and green-energy corridors in your target country.' },
  { Icon: BadgeCheck, q: 'Is this course on a shortage list?',         a: 'Course choice is cross-checked against demand-occupation lists in the UK, Canada, Australia, and Germany.' },
  { Icon: FileText,   q: 'How does this degree affect post-study work?', a: 'Each course is mapped to Graduate Route, PGWP, Temporary Graduate, Ireland TLG, or UAE residency — before you commit.' },
  { Icon: Compass,    q: 'Is the campus near actual hiring clusters?',   a: 'We avoid recommending towns where students travel 90 minutes to find a part-time job. City + employer geography matters.' },
];

const SUPPORT_STEPS = [
  { Icon: Compass,     title: 'Profile & Strategy',  desc: 'Match academics + budget to the right program and intake.' },
  { Icon: FileText,    title: 'Tests & Documents',   desc: 'IELTS/PTE prep, SOP, LORs, and finance alignment.' },
  { Icon: GraduationCap, title: 'Application & Offers', desc: 'Form handling, university comms, offer comparison.' },
  { Icon: Shield,      title: 'Visa File Design',    desc: 'Built to immigration logic — not just admission criteria.' },
  { Icon: Plane,       title: 'Post-Landing Setup',  desc: 'Accommodation, banking, registration, early job search.' },
  { Icon: Briefcase,   title: 'Post-Study Pathways', desc: 'Work visas, PR planning, skilled-professional transition.' },
];

const ETHICAL_POINTS = [
  'We will tell you clearly if your profile is too weak for a certain intake or tier — and recommend a realistic alternative instead of filing a risky case.',
  'We refuse to work on files with fake or manipulated documents — even if it means losing the lead.',
  'We disclose total costs upfront (tuition + living + visa) so families plan with real numbers, not optimistic guesses.',
  'We explain risks and limitations — job-market saturation, sector caps, immigration changes — instead of hiding them.',
];

const SectionHeading = ({ eyebrow, title, subtitle, light }) => (
  <div className={`max-w-3xl mb-8 lg:mb-12 ${light ? 'text-white' : ''}`}>
    {eyebrow && (
      <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full mb-3 ${
        light
          ? 'bg-[#C5A028]/15 border border-[#C5A028]/35 text-[#FACC15]'
          : 'bg-[#C5A028]/12 border border-[#C5A028]/35 text-[#C5A028]'
      } text-[10px] font-bold tracking-[0.22em] uppercase`}>
        {eyebrow}
      </div>
    )}
    <h2 className={`text-2xl sm:text-3xl lg:text-[36px] font-bold font-syne leading-tight mb-3 ${
      light ? 'text-white' : 'text-[#003087]'
    }`}>
      {title}
    </h2>
    {subtitle && (
      <p className={`leading-relaxed text-sm sm:text-base ${light ? 'text-slate-300' : 'text-slate-600'}`}>
        {subtitle}
      </p>
    )}
  </div>
);

const WhyChooseZafcoStudyAbroadPage = () => {
  usePageMeta(
    'Why Choose Zafco Study Abroad? | Migration Intelligence & Career Experts',
    'Discover the Zafco difference. Move beyond simple admissions with our MEA-approved, data-driven migration intelligence and career-first guidance for Indian students.',
    { canonical: '/study-abroad/why-choose-zafco/' },
  );

  // JSON-LD: BreadcrumbList
  useEffect(() => {
    const bc = {
      '@context': 'https://schema.org', '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home',         item: 'https://zafco.in/' },
        { '@type': 'ListItem', position: 2, name: 'Study Abroad', item: 'https://zafco.in/study-abroad/' },
        { '@type': 'ListItem', position: 3, name: 'Why Choose Zafco' },
      ],
    };
    const el = document.createElement('script');
    el.type = 'application/ld+json'; el.setAttribute('data-wcz-bc', '1'); el.text = JSON.stringify(bc);
    document.head.appendChild(el);
    return () => document.head.querySelector('script[data-wcz-bc]')?.remove();
  }, []);

  return (
    <div className="bg-white zaf-prose" data-testid="wcz-page">
      {/* ============ HERO ============ */}
      <section
        className="relative overflow-hidden text-white"
        style={{ background: 'linear-gradient(135deg, #003087 0%, #001F5C 55%, #020617 100%)' }}
      >
        <div aria-hidden="true" className="pointer-events-none absolute -top-32 -right-24 w-96 h-96 rounded-full bg-[#C5A028]/14 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-6 py-16 lg:py-28">
          <div className="max-w-3xl">
            <div className="flex flex-wrap items-center gap-3 mb-5">
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/8 border border-white/15 backdrop-blur-sm text-[10.5px] font-bold tracking-[0.22em] text-[#FACC15] uppercase">
                <Sparkles className="w-3.5 h-3.5" /> The Zafco Difference
              </span>
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/15 border border-emerald-400/40 text-[10.5px] font-bold tracking-[0.22em] text-emerald-200 uppercase">
                <Shield className="w-3.5 h-3.5" /> Govt of India · MEA Approved
              </span>
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-[54px] font-bold font-syne leading-[1.05] tracking-tight mb-5" data-testid="wcz-h1">
              Why Choose Zafco <span className="text-[#FACC15]">Study Abroad?</span>
            </h1>
            <p className="text-lg sm:text-xl text-slate-200 leading-snug max-w-2xl mb-3 font-semibold">
              Built on Global Mobility — not just Admissions.
            </p>
            <p className="text-sm sm:text-base text-slate-400 leading-relaxed max-w-2xl mb-7">
              Most consultancies are built as <strong className="text-white">admission agencies</strong>. Zafco is built as a <strong className="text-[#FACC15]">global mobility and career migration partner</strong>. That single difference changes how every Indian student&apos;s case is handled.
            </p>
            <div className="flex flex-wrap gap-3">
              <a
                href="#career-strategy-session"
                className="inline-flex items-center gap-2 bg-[#C5A028] hover:bg-[#EAB308] text-black font-bold text-sm sm:text-base px-7 py-3.5 rounded-full transition-all hover:-translate-y-0.5 shadow-lg shadow-[#C5A028]/30"
                data-testid="wcz-hero-cta"
              >
                Book Your Career Strategy Session <ArrowRight className="w-4 h-4" />
              </a>
              <Link
                to="/study-abroad/about-zafco/"
                className="inline-flex items-center gap-2 border-2 border-white/30 hover:border-white text-white font-semibold text-sm sm:text-base px-7 py-3.5 rounded-full transition-all"
              >
                About Zafco Study Abroad
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ============ SIDE-BY-SIDE COMPARISON (The Hook) ============ */}
      <section className="bg-slate-50 py-14 lg:py-20">
        <div className="max-w-7xl mx-auto px-6">
          <SectionHeading
            eyebrow="The Category Difference"
            title="Admissions Agency vs Global Mobility Partner"
            subtitle="Six dimensions where the typical study-abroad agency model breaks down — and where Zafco starts."
          />
          <div className="grid lg:grid-cols-2 gap-5" data-testid="wcz-comparison">
            {/* TYPICAL agency */}
            <div className="rounded-2xl border-2 border-slate-200 bg-white p-6 sm:p-7 relative overflow-hidden" data-testid="wcz-comparison-typical">
              <div className="absolute -top-6 -right-6 w-32 h-32 rounded-full bg-slate-100 opacity-50" aria-hidden="true" />
              <div className="relative">
                <div className="flex items-center gap-3 mb-5 pb-4 border-b border-slate-200">
                  <div className="w-11 h-11 rounded-full bg-slate-100 border border-slate-200 flex items-center justify-center">
                    <X className="w-5 h-5 text-slate-500" aria-hidden="true" />
                  </div>
                  <div>
                    <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-slate-500">Typical Agency</div>
                    <div className="text-base font-bold font-syne text-slate-800">Admissions Model</div>
                  </div>
                </div>
                <dl className="space-y-3">
                  {COMPARISON.map(c => (
                    <div key={c.dim} className="flex items-start gap-3">
                      <dt className="w-24 sm:w-28 text-[10px] font-bold tracking-[0.16em] text-slate-400 uppercase shrink-0 pt-0.5">{c.dim}</dt>
                      <dd className="text-sm text-slate-600 leading-relaxed">{c.a}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            </div>

            {/* ZAFCO */}
            <div className="rounded-2xl border-2 border-[#C5A028] bg-gradient-to-br from-amber-50/40 via-white to-amber-50/40 p-6 sm:p-7 relative overflow-hidden shadow-lg" data-testid="wcz-comparison-zafco">
              <div className="absolute -top-6 -right-6 w-32 h-32 rounded-full bg-[#C5A028]/15 blur-2xl" aria-hidden="true" />
              <div className="relative">
                <div className="flex items-center gap-3 mb-5 pb-4 border-b border-amber-200/60">
                  <div className="w-11 h-11 rounded-full bg-[#C5A028]/15 border border-[#C5A028]/40 flex items-center justify-center">
                    <CheckCircle2 className="w-5 h-5 text-[#C5A028]" aria-hidden="true" />
                  </div>
                  <div>
                    <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-[#C5A028]">Zafco Study Abroad</div>
                    <div className="text-base font-bold font-syne text-[#003087]">Global Mobility Partner</div>
                  </div>
                </div>
                <dl className="space-y-3">
                  {COMPARISON.map(c => (
                    <div key={c.dim} className="flex items-start gap-3">
                      <dt className="w-24 sm:w-28 text-[10px] font-bold tracking-[0.16em] text-[#C5A028] uppercase shrink-0 pt-0.5">{c.dim}</dt>
                      <dd className="text-sm text-slate-700 leading-relaxed font-medium">{c.z}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ TRUST & COMPLIANCE BOX ============ */}
      <section className="bg-white py-14 lg:py-20">
        <div className="max-w-5xl mx-auto px-6">
          <SectionHeading
            eyebrow="Compliance & Approvals"
            title="MEA-Approved. Transparent. Accountable."
            subtitle="Indian families are right to worry about fraud, refusals, and changing rules. Zafco addresses that with formal approvals and transparent systems — not promises."
          />
          <div className="rounded-3xl border-2 border-emerald-300/40 bg-gradient-to-br from-emerald-50/60 to-white p-6 sm:p-9 shadow-md" data-testid="wcz-compliance-box">
            <div className="grid sm:grid-cols-[auto_1fr] gap-5 sm:gap-7 items-start">
              <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-emerald-100 border border-emerald-300 flex items-center justify-center mx-auto sm:mx-0 shrink-0">
                <BadgeCheck className="w-9 h-9 sm:w-10 sm:h-10 text-emerald-700" aria-hidden="true" />
              </div>
              <div>
                <div className="text-[10px] font-bold tracking-[0.22em] text-emerald-700 uppercase mb-1">Government of India · MEA Approved</div>
                <div className="text-base font-bold font-syne text-[#003087] mb-3">RC: B-0762/DEL/PER/1000+/5/9148/2015</div>
                <ul className="space-y-2.5">
                  <li className="flex items-start gap-2 text-sm text-slate-700"><CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" /> Your file is managed within a regulated framework — not by a casual consultancy.</li>
                  <li className="flex items-start gap-2 text-sm text-slate-700"><CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" /> All fees, services, and timelines documented before you sign — no surprise charges.</li>
                  <li className="flex items-start gap-2 text-sm text-slate-700"><CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" /> Only genuine documentation — no fake bank statements, no manufactured experience.</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ MIGRATION INTELLIGENCE (4 cards) ============ */}
      <section
        className="relative overflow-hidden text-white py-14 lg:py-20"
        style={{ background: 'linear-gradient(135deg, #003087 0%, #001F5C 60%, #020617 100%)' }}
      >
        <div aria-hidden="true" className="pointer-events-none absolute -top-32 -left-24 w-96 h-96 rounded-full bg-[#C5A028]/14 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-6">
          <SectionHeading
            light
            eyebrow="Our Edge"
            title="Migration Intelligence — Not a Course Catalog."
            subtitle="Instead of asking only &lsquo;Can you get admission?&rsquo;, we ask four data-driven questions that ordinary education agents skip."
          />
          <div className="grid sm:grid-cols-2 gap-4 mb-10" data-testid="wcz-migration-grid">
            {MIGRATION_QUESTIONS.map((m, idx) => (
              <article
                key={m.q}
                className="rounded-2xl bg-white/5 border border-white/10 backdrop-blur-sm p-5 sm:p-6 hover:border-[#C5A028]/40 hover:bg-white/8 transition-all"
                data-testid={`wcz-mi-card-${idx}`}
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-xl bg-[#C5A028]/15 border border-[#C5A028]/40 flex items-center justify-center shrink-0">
                    <m.Icon className="w-5 h-5 text-[#FACC15]" aria-hidden="true" />
                  </div>
                  <div className="text-[10px] font-bold tracking-[0.2em] text-[#FACC15] uppercase">Question {idx + 1}</div>
                </div>
                <h3 className="text-base sm:text-lg font-bold font-syne text-white mb-2 leading-tight">{m.q}</h3>
                <p className="text-sm text-slate-300 leading-relaxed">{m.a}</p>
              </article>
            ))}
          </div>

          {/* Mid-page CTA */}
          <div className="rounded-3xl bg-gradient-to-r from-[#C5A028]/15 via-white/5 to-[#C5A028]/15 border border-[#C5A028]/30 p-5 sm:p-7 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <div className="text-[10px] font-bold tracking-[0.22em] text-[#FACC15] uppercase mb-1">Strategy Session</div>
              <p className="text-base sm:text-lg font-syne font-bold text-white leading-snug max-w-xl">
                Want this Migration Intelligence applied to <em>your</em> profile?
              </p>
            </div>
            <a
              href="#career-strategy-session"
              className="inline-flex items-center gap-2 bg-[#C5A028] hover:bg-[#EAB308] text-black font-bold text-sm px-6 py-3 rounded-full whitespace-nowrap transition-all hover:-translate-y-0.5"
              data-testid="wcz-mid-cta"
            >
              Book Your Career Strategy Session <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      {/* ============ INLINE LEAD FORM — Career Strategy Session ============ */}
      <section
        className="relative overflow-hidden text-white py-14 lg:py-20"
        style={{ background: 'linear-gradient(180deg, #020617 0%, #001F5C 50%, #003087 100%)' }}
        data-testid="wcz-inline-form-section"
        id="career-strategy-session"
      >
        <div aria-hidden="true" className="pointer-events-none absolute top-1/2 -translate-y-1/2 -right-32 w-[28rem] h-[28rem] rounded-full bg-[#C5A028]/12 blur-3xl" />
        <div aria-hidden="true" className="pointer-events-none absolute -bottom-32 -left-24 w-96 h-96 rounded-full bg-[#003087]/40 blur-3xl" />

        <div className="relative max-w-6xl mx-auto px-6">
          <div className="grid lg:grid-cols-[1fr_1.1fr] gap-8 lg:gap-12 items-center">
            {/* LEFT — Value props */}
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#C5A028]/15 border border-[#C5A028]/40 text-[10.5px] font-bold tracking-[0.22em] text-[#FACC15] uppercase mb-4">
                <Sparkles className="w-3.5 h-3.5" /> 1-on-1 · 30 min · No Cost
              </div>
              <h2 className="text-2xl sm:text-3xl lg:text-[40px] font-bold font-syne text-white leading-[1.1] mb-4">
                Get Your <span className="text-[#FACC15]">Free Career Strategy Session</span> — straight from this page.
              </h2>
              <p className="text-sm sm:text-base text-slate-300 leading-relaxed mb-6">
                Skip the back-and-forth. Share your profile in 30 seconds and a senior counsellor will personally map a country + course + visa plan for you — based on real 2026 labour-market and immigration data.
              </p>
              <ul className="space-y-3" data-testid="wcz-inline-form-bullets">
                <li className="flex items-start gap-3 text-sm sm:text-[15px] text-slate-200">
                  <CheckCircle2 className="w-5 h-5 text-[#FACC15] mt-0.5 shrink-0" aria-hidden="true" />
                  <span><strong className="text-white">Migration Intelligence applied to your profile</strong> — shortage lists, PR pathways, sector demand.</span>
                </li>
                <li className="flex items-start gap-3 text-sm sm:text-[15px] text-slate-200">
                  <CheckCircle2 className="w-5 h-5 text-[#FACC15] mt-0.5 shrink-0" aria-hidden="true" />
                  <span><strong className="text-white">Honest assessment</strong> — including realistic alternatives if your profile isn&apos;t ready for your first-choice intake.</span>
                </li>
                <li className="flex items-start gap-3 text-sm sm:text-[15px] text-slate-200">
                  <CheckCircle2 className="w-5 h-5 text-[#FACC15] mt-0.5 shrink-0" aria-hidden="true" />
                  <span><strong className="text-white">Transparent costs</strong> — tuition + living + visa, disclosed before you sign anything.</span>
                </li>
              </ul>
              <div className="mt-7 flex flex-wrap items-center gap-4 text-[11px] text-slate-400">
                <span className="inline-flex items-center gap-1.5"><Shield className="w-3.5 h-3.5 text-emerald-400" /> MEA Approved</span>
                <span className="inline-flex items-center gap-1.5"><BadgeCheck className="w-3.5 h-3.5 text-emerald-400" /> No spam, no obligation</span>
                <span className="inline-flex items-center gap-1.5"><Phone className="w-3.5 h-3.5 text-[#FACC15]" /> Reply within 60 seconds on WhatsApp</span>
              </div>
            </div>

            {/* RIGHT — Form card */}
            <div
              className="rounded-3xl bg-white/8 backdrop-blur-md border border-white/15 p-5 sm:p-7 shadow-2xl shadow-[#020617]/50 relative"
              data-testid="wcz-inline-form-card"
            >
              <div className="absolute -top-3 left-6 inline-flex items-center gap-2 bg-[#C5A028] text-black text-[10px] font-bold tracking-[0.22em] uppercase px-3 py-1 rounded-full shadow-md">
                <Sparkles className="w-3 h-3" /> Strategy Session
              </div>
              <div className="mb-4 pt-1">
                <h3 className="text-xl sm:text-2xl font-bold font-syne text-white leading-tight">Tell us about your goal.</h3>
                <p className="text-xs sm:text-sm text-slate-400 mt-1">Counsellor will respond within 60 seconds via WhatsApp.</p>
              </div>
              <ConsultationForm variant="dark" source="study-abroad-why-zafco-inline-form" />
            </div>
          </div>
        </div>
      </section>

      {/* ============ COUNTRY-NEUTRAL GUIDANCE ============ */}
      <section className="bg-white py-14 lg:py-20">
        <div className="max-w-5xl mx-auto px-6">
          <SectionHeading
            eyebrow="Country-Neutral, Career-First"
            title="We Don&rsquo;t Push Countries. We Match Careers."
            subtitle="Zafco does not promote one country because &lsquo;everyone is going there this year.&rsquo; Counsellors are trained to be country-neutral and career-first."
          />
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3" data-testid="wcz-country-grid">
            {COUNTRIES.map(c => (
              <Link
                key={c.name}
                to={c.href}
                className="group flex flex-col items-center gap-2 p-4 rounded-xl bg-slate-50 border border-slate-200 hover:border-[#C5A028] hover:bg-amber-50/40 hover:-translate-y-0.5 transition-all"
                data-testid={`wcz-country-link-${c.name.toLowerCase()}`}
              >
                <span className="text-3xl">{c.flag}</span>
                <span className="text-sm font-bold font-syne text-[#003087] group-hover:text-[#C5A028] transition-colors">{c.name}</span>
                <span className="text-[10px] text-slate-500 group-hover:text-[#C5A028] transition-colors inline-flex items-center gap-1">
                  Explore <ArrowUpRight className="w-2.5 h-2.5" />
                </span>
              </Link>
            ))}
          </div>
          <p className="text-sm text-slate-500 leading-relaxed mt-6 text-center max-w-2xl mx-auto">
            The goal is not to send you abroad <em>at any cost</em>. The goal is to maximise your{' '}
            <strong className="text-[#003087]">return on investment</strong> across realistic earnings, visa stability, and global career mobility.
          </p>
        </div>
      </section>

      {/* ============ END-TO-END SUPPORT (6-step process flow) ============ */}
      <section className="bg-slate-50 py-14 lg:py-20">
        <div className="max-w-7xl mx-auto px-6">
          <SectionHeading
            eyebrow="Support Lifecycle"
            title="From Shortlisting to Settling In."
            subtitle="A lot of agencies disappear after you get your visa. Zafco is structured for the entire lifecycle — one partner, six stages."
          />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="wcz-support-flow">
            {SUPPORT_STEPS.map((s, idx) => (
              <div
                key={s.title}
                className="relative rounded-2xl bg-white border border-slate-200 hover:border-[#C5A028]/50 hover:shadow-md transition-all p-5"
                data-testid={`wcz-step-${idx}`}
              >
                <div className="absolute top-4 right-4 text-[10px] font-bold tracking-[0.18em] text-slate-300">STAGE {String(idx + 1).padStart(2, '0')}</div>
                <div className="w-11 h-11 rounded-xl bg-[#C5A028]/12 border border-[#C5A028]/30 flex items-center justify-center mb-3">
                  <s.Icon className="w-5 h-5 text-[#C5A028]" aria-hidden="true" />
                </div>
                <h3 className="text-base font-bold font-syne text-[#003087] mb-2">{s.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ ETHICAL CONSULTING — high-contrast callout ============ */}
      <section className="bg-white py-14 lg:py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-[1fr_1.5fr] gap-8 items-start">
            <div className="lg:sticky lg:top-24">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#C5A028]/12 border border-[#C5A028]/35 text-[10px] font-bold tracking-[0.22em] text-[#C5A028] uppercase mb-3">
                Honest Filters
              </div>
              <h2 className="text-2xl sm:text-3xl lg:text-[36px] font-bold font-syne text-[#003087] leading-tight mb-4">
                Ethical Consulting — Even When We Say <em>&ldquo;Not Yet.&rdquo;</em>
              </h2>
              <p className="text-sm sm:text-base text-slate-600 leading-relaxed mb-5">
                Our commitment is to ethical consulting even when that means losing a lead. This builds long-term trust — and is exactly the transparent behaviour search engines now reward.
              </p>
              <div className="rounded-2xl bg-amber-50/60 border border-amber-200 p-4 flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-700 mt-0.5 shrink-0" aria-hidden="true" />
                <p className="text-xs text-amber-900 leading-relaxed">
                  <strong>If your profile is too weak for a certain intake or tier</strong>, we tell you — and recommend a realistic alternative instead of filing a risky case.
                </p>
              </div>
            </div>

            <div className="rounded-3xl bg-[#003087] text-white p-6 sm:p-9 relative overflow-hidden" data-testid="wcz-ethical-callout">
              <div aria-hidden="true" className="pointer-events-none absolute -top-20 -right-20 w-64 h-64 rounded-full bg-[#C5A028]/15 blur-3xl" />
              <Quote className="w-10 h-10 text-[#C5A028] mb-5 opacity-60" aria-hidden="true" />
              <ul className="space-y-4 relative" data-testid="wcz-ethical-list">
                {ETHICAL_POINTS.map((p, idx) => (
                  <li key={p} className="flex items-start gap-3" data-testid={`wcz-ethical-${idx}`}>
                    <CheckCircle2 className="w-5 h-5 text-[#FACC15] mt-0.5 shrink-0" aria-hidden="true" />
                    <p className="text-sm sm:text-[15px] text-slate-100 leading-relaxed">{p}</p>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ============ DATA-BACKED SUCCESS ============ */}
      <section className="bg-slate-50 py-14 lg:py-20">
        <div className="max-w-6xl mx-auto px-6">
          <SectionHeading
            eyebrow="Visa Approval Methodology"
            title="We Treat Visas Like a Science — Not a Gamble."
            subtitle="Every file goes through three internal checkpoints before it leaves Zafco&rsquo;s desk. The outcome: a consistently high visa-approval rate across major destinations."
          />
          <div className="grid sm:grid-cols-3 gap-4" data-testid="wcz-science-grid">
            <div className="rounded-2xl bg-white border border-slate-200 p-5">
              <FileText className="w-7 h-7 text-[#C5A028] mb-3" aria-hidden="true" />
              <h3 className="text-base font-bold font-syne text-[#003087] mb-2">SOP &amp; GTE/GS Review</h3>
              <p className="text-sm text-slate-600 leading-relaxed">Narrative must be logical and evidence-based — not a recycled template.</p>
            </div>
            <div className="rounded-2xl bg-white border border-slate-200 p-5">
              <AlertTriangle className="w-7 h-7 text-[#C5A028] mb-3" aria-hidden="true" />
              <h3 className="text-base font-bold font-syne text-[#003087] mb-2">Early Red-Flag Detection</h3>
              <p className="text-sm text-slate-600 leading-relaxed">Gaps, prior refusals, inconsistent finances, unusual course jumps — caught before lodging.</p>
            </div>
            <div className="rounded-2xl bg-white border border-slate-200 p-5">
              <Compass className="w-7 h-7 text-[#C5A028] mb-3" aria-hidden="true" />
              <h3 className="text-base font-bold font-syne text-[#003087] mb-2">Country-Specific Structuring</h3>
              <p className="text-sm text-slate-600 leading-relaxed">What matters for UK ≠ Canada ≠ Australia ≠ Ireland ≠ Dubai — each file structured accordingly.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ============ COMMUNITY / EVENTS ============ */}
      <section className="bg-white py-14 lg:py-20">
        <div className="max-w-7xl mx-auto px-6">
          <SectionHeading
            eyebrow="Beyond Counselling"
            title="Community, Events &amp; Real-World Insight."
            subtitle="One-to-one counselling is reinforced with live alumni sessions, university Q&amp;A, and labour-market-trend webinars — so your decisions stay grounded in real, current evidence."
          />
          <div className="grid sm:grid-cols-3 gap-4" data-testid="wcz-community-grid">
            <div className="rounded-2xl bg-slate-50 border border-slate-200 p-5">
              <Users className="w-7 h-7 text-[#C5A028] mb-3" aria-hidden="true" />
              <h3 className="text-base font-bold font-syne text-[#003087] mb-2">Alumni Live Sessions</h3>
              <p className="text-sm text-slate-600 leading-relaxed">Hear from students currently on Graduate Route, PGWP, Australia 485, Ireland TLG, and UAE residency.</p>
            </div>
            <div className="rounded-2xl bg-slate-50 border border-slate-200 p-5">
              <GraduationCap className="w-7 h-7 text-[#C5A028] mb-3" aria-hidden="true" />
              <h3 className="text-base font-bold font-syne text-[#003087] mb-2">University Q&amp;A</h3>
              <p className="text-sm text-slate-600 leading-relaxed">Direct conversations with admissions teams on entry, scholarships, and employability.</p>
            </div>
            <div className="rounded-2xl bg-slate-50 border border-slate-200 p-5">
              <Calendar className="w-7 h-7 text-[#C5A028] mb-3" aria-hidden="true" />
              <h3 className="text-base font-bold font-syne text-[#003087] mb-2">Themed Workshops</h3>
              <p className="text-sm text-slate-600 leading-relaxed">&ldquo;Best Courses for PR,&rdquo; &ldquo;2026 Labour-Market Trends,&rdquo; &ldquo;How to Read a Shortage List.&rdquo;</p>
            </div>
          </div>
        </div>
      </section>

      {/* ============ FINAL CTA ============ */}
      <section className="text-white" style={{ background: 'linear-gradient(135deg, #003087 0%, #001F5C 60%, #020617 100%)' }}>
        <div className="max-w-4xl mx-auto px-6 py-14 lg:py-20 text-center" data-testid="wcz-final-cta-section">
          <Brain className="w-12 h-12 text-[#FACC15] mx-auto mb-5" aria-hidden="true" />
          <h2 className="text-2xl md:text-4xl font-bold font-syne mb-4 leading-tight">
            Pick the partner who runs <span className="text-[#FACC15]">Migration Intelligence</span>, not a course catalog.
          </h2>
          <p className="text-slate-300 max-w-2xl mx-auto mb-7 leading-relaxed">
            One session is enough to see the difference. Bring your profile — academics, budget, target intake — and we&apos;ll map a country + course + city plan that fits your career, not just your admissions calendar.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link
              to="/study-abroad/book-counselling/?source=study-abroad-why-zafco-final-cta"
              className="inline-flex items-center justify-center gap-2 bg-[#C5A028] hover:bg-[#EAB308] text-black font-bold px-7 py-3.5 rounded-full transition-all hover:-translate-y-0.5 shadow-lg shadow-[#C5A028]/30"
              data-testid="wcz-final-cta"
            >
              Book Your Career Strategy Session <ArrowUpRight className="w-4 h-4" />
            </Link>
            <Link
              to="/study-abroad/free-profile-evaluation/?source=study-abroad-why-zafco-final-cta"
              className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/15 text-white border border-white/20 font-semibold px-7 py-3.5 rounded-full transition-colors"
              data-testid="wcz-final-evaluation"
            >
              Get a Free Profile Evaluation
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default WhyChooseZafcoStudyAbroadPage;
